import React from 'react';
import equipmentImage from '../assets/images/berendezes.png';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheck, FaCog, FaIndustry, FaHome } from 'react-icons/fa';

const BerendezesGyartas: React.FC = () => {
  const services = [
    {
      icon: <FaCog className="text-3xl text-gray-600" />,
      title: "Egyedi tervezés",
      description: "Személyre szabott villamos berendezések tervezése és fejlesztése.",
      features: ["Igényfelmérés", "3D tervezés", "Prototípus készítés", "Optimalizálás"]
    },
    {
      icon: <FaIndustry className="text-3xl text-blue-600" />,
      title: "Ipari megoldások",
      description: "Nagyipari és középipari berendezések gyártása.",
      features: ["Kapcsolószekrények", "Vezérlőpanelek", "Automatizálás", "Rendszerintegráció"]
    },
    {
      icon: <FaHome className="text-3xl text-green-600" />,
      title: "Lakossági termékek",
      description: "Háztartási és kisvállalkozási célú berendezések.",
      features: ["Elosztótáblák", "Vezérlőegységek", "Mérőberendezések", "Biztonsági eszközök"]
    }
  ];

  const advantages = [
    "Egyedi tervezés",
    "Minőségi kivitelezés",
    "Teljes körű támogatás",
    "Garanciális szolgáltatás",
    "Gyors átfutási idő",
    "Versenyképes árak"
  ];

  const products = [
    {
      category: "Ipari berendezések",
      items: ["Kapcsolószekrények", "Vezérlőpanelek", "Elosztóberendezések", "Mérőeszközök"]
    },
    {
      category: "Automatizálás",
      items: ["PLC programozás", "HMI rendszerek", "Érzékelők", "Riasztórendszerek"]
    },
    {
      category: "Speciális megoldások",
      items: ["Egyedi kábelezés", "Védőrelék", "Transzformátorok", "Szünetmentes tápok"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-gray-600 to-gray-800 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={equipmentImage} 
            alt="Berendezés gyártás" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Berendezés Gyártás
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Egyedi villamos berendezések ipari és lakossági célokra
            </p>
            <a 
              href="/#contact"
              className="bg-white text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Ajánlatot kérek
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Szolgáltatásaink
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-center">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-4 text-gray-700">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <FaCheck className="text-green-600 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Termékek és megoldások
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-gray-700">{product.category}</h3>
                <ul className="space-y-2">
                  {product.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <FaCheck className="text-blue-600 mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Munkamenetünk
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Konzultáció", desc: "Igények felmérése és specifikáció" },
              { step: "2", title: "Tervezés", desc: "Részletes műszaki dokumentáció" },
              { step: "3", title: "Gyártás", desc: "Precíz kivitelezés minőségi anyagokból" },
              { step: "4", title: "Átadás", desc: "Telepítés, beüzemelés és oktatás" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gray-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Előnyeink
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow">
                <FaCheck className="text-green-600 mr-3 text-xl" />
                <span className="text-gray-800 font-medium">{advantage}</span>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Contact Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            Kérjen Árajánlatot!
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Elérhetőségeink</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📞
                  </div>
                  <div>
                    <div className="font-medium">Nagy Péter Villanyszerelő mester</div>
                    <a 
                      href="tel:+36703620652"
                      className="text-red-400 hover:text-red-300 font-semibold transition-colors"
                    >
                      +36703620652
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📞
                  </div>
                  <div>
                    <div className="font-medium">Tamasi Krisztán villamosbiztonsági felülvizsgáló</div>
                    <a 
                      href="tel:+36706049889"
                      className="text-red-400 hover:text-red-300 font-semibold transition-colors"
                    >
                      +36706049889
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    ✉️
                  </div>
                  <div>
                    <a 
                      href="mailto:kelectrokft@gmail.com"
                      className="hover:text-red-400 transition-colors"
                    >
                      kelectrokft@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📍
                  </div>
                  <div>
                    <span>SZOLNOK és 100km-es körzete</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Gyors kapcsolatfelvétel</h3>
              <p className="text-gray-300 mb-4">
                Hívjon minket bizalommal! Ingyenes helyszíni felméréssel és 
                részletes árajánlattal várjuk megkeresését.
              </p>
              <div className="space-y-2">
                <p className="text-red-400 font-semibold">📞 Azonnali válasz</p>
                <p className="text-red-400 font-semibold">📋 Ingyenes felmérés</p>
                <p className="text-red-400 font-semibold">💰 Versenyképes árak</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BerendezesGyartas; 